import React from 'react';
import { TeamlessCollaborationPromptPopover } from './TeamlessCollaborationPromptPopover';
import { useTeamlessCollaborationPromptQuery } from '@trello/graphql/generated';
import { TeamlessCollaborationPromptPopoverId } from 'app/gamma/src/components/popovers/popover-ids';
import { PopoverProps, usePopover } from 'app/gamma/src/components/popover';
import { Noun, Verb, trackUe } from '@trello/analytics';

interface TeamlessCollaborationPromptProps {
  boardId: string;
  onClose: () => void;
  onTeamCreated: () => void;
  target: PopoverProps['target'];
}

export const TeamlessCollaborationPrompt: React.FunctionComponent<TeamlessCollaborationPromptProps> = ({
  boardId,
  onClose,
  onTeamCreated,
  target,
}) => {
  const { isPopoverShown, showPopover } = usePopover({
    name: TeamlessCollaborationPromptPopoverId,
  });

  const { data } = useTeamlessCollaborationPromptQuery({
    variables: { boardId },
  });

  /**
   * For the prompt to be shown, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has at least 3 confirmed members
   * - The user is a board admin
   * - The user is not a member of an enterprise team
   * If the user is an enterprise team member, the Private Garden rules would
   * prevent the goal of this feature- creating a free team.
   */
  const meId = data && data.member && data.member.id;
  const meMembership =
    data &&
    data.board &&
    data.board.memberships &&
    data.board.memberships.find((membership) => membership.idMember === meId);
  const isBoardAdmin = meMembership && meMembership.memberType === 'admin';
  const isEnterpriseMember =
    data &&
    data.member &&
    data.member.enterprises &&
    data.member.enterprises.length > 0;
  const isTeamless = data && data.board && !data.board.idOrganization;
  const numberOfConfirmedCollaborators =
    data &&
    data.board &&
    data.board.members &&
    data.board.members.reduce((count, member) => {
      if (member.confirmed) {
        count = count + 1;
      }
      return count;
    }, 0);
  const hasEnoughCollaborators =
    numberOfConfirmedCollaborators && numberOfConfirmedCollaborators >= 3;
  const boardAndMemberMeetRequirements =
    isBoardAdmin && isTeamless && hasEnoughCollaborators && !isEnterpriseMember;

  if (boardAndMemberMeetRequirements && !isPopoverShown) {
    showPopover();
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.VIEWS,
      directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
    });
  }

  return isPopoverShown ? (
    <TeamlessCollaborationPromptPopover
      boardId={boardId}
      onClose={onClose}
      onTeamCreated={onTeamCreated}
      target={target}
    />
  ) : null;
};
